import CropFreeIcon from '@mui/icons-material/CropFree'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Router from 'next/router'
import React, { useEffect } from 'react'
import CardImage from 'src/components/CardImage'
import PromoBadge, { promoBadgeContent } from 'src/components/Promo/PromoBadge'
import { useFilterProvider } from 'src/context/FilterProvider'
import useBreakpoints from 'src/hooks/useBreakpoints'
import useCardPrice from 'src/hooks/useCardPrice'
import useCommon from 'src/hooks/useCommon'
import useConfigSummary from 'src/hooks/useConfigSummary'
import { getCoverPhotoUrl, getDiscount } from 'src/utils/coverPhoto'
import { operatorPromoPill } from 'src/utils/operatorPromo'
import ga from 'src/utils/pixels/gtag'
import { ILocation } from '../../types/graphql'
import { numberRounedUpWithoutDecimals } from '../../utils'
import VerifiedBadge from '../VerifiedBadge'

/** Surfaces that mount the shared `<RecommendedSection>` carousel. */
export type RecommendedCarouselSurface =
  | 'home_recommended'
  | 'pillar_recommended'
  | 'blog_recommended'
  | 'flotillas_recommended'
  | 'size_guide_recommended'

/** Mount point of the card. REQUIRED — this component renders in nine
 *  unrelated places and `recommended_card_click` is useless without knowing
 *  which one drove the click. Never default it inline; a new mount point adds
 *  a new literal here. The search-results grid uses a different component
 *  (`LocationSearchCard/SearchCard.tsx`) with its own `search_result_click` —
 *  do NOT merge the two vocabularies.
 *
 *  `host_preview` is the add-listing / edit-listing cover preview. Those
 *  callers pass `disableLink`, so the card returns before the click handler
 *  is attached and NO event ever fires from them — the literal exists only to
 *  satisfy the required prop and to document the mount. */
export type RecommendedCardSurface =
  | RecommendedCarouselSurface
  | 'listing_similar'
  | 'landing_storages'
  | 'directory'
  | 'host_preview'

/** Arrow position for a carousel of these cards (`styleDefault={false}`):
 *  the middle of the 16:9 photo — the slide's 8px top padding plus half the
 *  photo of the 270 / 300 / 280px-wide card. At the carousel's middle the
 *  arrows covered the promo badge at the photo's bottom edge. */
export const CARD_CAROUSEL_ARROW_TOP = 'top-[84px] sm:top-[92px] md:top-[87px]'

interface SpotCardProps {
  location: ILocation
  surface: RecommendedCardSurface
  styleDefault?: boolean
  toggleLocation?: (location: ILocation | undefined) => void
  hidePrice?: boolean
  disableLink?: boolean
  simpleLink?: boolean
}

const SpotCard = ({
  location,
  surface,
  styleDefault = true,
  toggleLocation,
  hidePrice,
  disableLink = false,
  simpleLink = false
}: SpotCardProps) => {
  const { t } = useTranslation('common')
  const { isUpMd, isDownMd } = useBreakpoints()
  const { defaultConfiguration, shortAddress } = location
  const { canStoreVehicle } = defaultConfiguration ?? {}
  const isParking = canStoreVehicle === true
  const { urlRoute } = defaultConfiguration ?? {}
  const { hasMultipleConfigurations, numConfigurations, sizeRangeText } =
    useConfigSummary(location)
  const { locMap, showSize } = useCommon()
  const locType = locMap(location)

  const toggle = toggleLocation && isUpMd ? toggleLocation : () => {}

  const { filters, shallowParams, isReady, isSliderOpen } = useFilterProvider()

  const getUrl = (args?: { [key: string]: string }) => {
    const entries = Object.entries({
      ...shallowParams,
      ...filters,
      ...args,
      sliderOpen: isSliderOpen
    }).filter(([, v]) => v !== undefined && v !== null)
    const params = new URLSearchParams(entries as string[][])
    const url = isUpMd ? (urlRoute ?? '') : `${urlRoute}?${params.toString()}`
    return url
  }

  const [isClicked, setIsClicked] = React.useState(false)
  // JS-enhanced navigation for the mobile <Link> wrapper. Pre-hydration
  // the native anchor href on the Link takes over (preserving filter
  // params via the SSR'd `url`); this runs only after JS is bound to
  // honor scroll-restoration on back-nav. PostHog dead-click data
  // showed the previous bare-div pattern was the source of the bulk of
  // pillar-page card dead clicks — see SearchCard.tsx for the parallel
  // fix on the search-results variant.
  const doNavigate = () => {
    const url = getUrl({ scrollY: location.id })
    Router.push(url ?? '')
    setIsClicked(true)
  }

  const onMobileLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isUpMd) return
    if (simpleLink) return
    // Honor modifier-click / middle-click affordances.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return
    }
    e.preventDefault()
    doNavigate()
  }

  const url = simpleLink ? (urlRoute ?? '') : getUrl()
  const sizeText = sizeRangeText ?? showSize(location, true)
  const { price, isBrokerMode, showFromPrice } = useCardPrice(location)
  const discount = getDiscount(defaultConfiguration, isBrokerMode)
  // The operator's own promotion (Traditional only — never co-occurs with
  // `discount`, which is SpotMe Now only).
  const promoPill = operatorPromoPill(defaultConfiguration?.operatorPromo)
  const promoBadge = promoBadgeContent(discount, promoPill)

  const ref = React.useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isReady || !isDownMd) return
    if (!isSliderOpen) return
    if (!ref.current) return
    if (isClicked) return

    const isInViewWindow = ref.current.getBoundingClientRect().top < 0
    if (isInViewWindow) return

    if (shallowParams.scrollY === location.id) {
      ref.current.scrollIntoView({ behavior: 'instant', block: 'center' })
    }
  }, [isClicked, isDownMd, isReady, location.id, shallowParams, isSliderOpen])

  const [iconBig, value, unit] = isBrokerMode
    ? [
        <CropFreeIcon className="mt-[-2.5px] mr-1 text-lg" key="iconBig" />,
        price,
        '/m²'
      ]
    : [null, price, `/${t('FILTER.MONTH')}`]

  const cardContent = (
    <div
      ref={ref}
      className={`${
        styleDefault
          ? 'location-search-card'
          : 'location-search-card max-w-[270px] min-w-[270px] sm:max-w-[300px] sm:min-w-[300px] md:max-w-[280px] md:min-w-[280px]'
      } overflow-hidden rounded-2xl border border-ink-100/15 bg-white-500 transition-all duration-300 hover:-translate-y-0.5 hover:border-ink-100/40`}
      onMouseEnter={() => toggle(location)}
      onMouseLeave={() => toggle(undefined)}
    >
      <div
        className={`relative w-full overflow-hidden ${
          styleDefault ? 'aspect-[16/9] md:aspect-[16/10]' : 'aspect-[16/9]'
        }`}
        key={location.id}
      >
        <CardImage
          src={getCoverPhotoUrl(location)}
          alt={`${locType} ${sizeText} ${location.id}`}
        />
        {/* Verified-by-SpotMe overlay — top-left of image */}
        <VerifiedBadge verified={location.isVerified} variant="overlay" />
        {/* Discount pill lives on the photo, not in the price row: next to a
            "Desde $1,465/mes" price it wrapped to two lines and made the
            card taller than its carousel neighbours (prod, 2026-09-25). The
            image box is aspect-fixed, so the pill can never change height. */}
        {promoBadge && (
          <div className="pointer-events-none absolute right-2 bottom-2 left-2 z-10 flex">
            <PromoBadge promo={promoBadge} placement="overlay" />
          </div>
        )}
        {/* Right-side overlay pill — same parking-aware logic as SearchCard:
            hide on parking single-config (m² is uninformative), show spot
            count on parking multi-config, otherwise size (range) + count. */}
        {isParking && !hasMultipleConfigurations ? null : (
          <div className="absolute top-2 right-2 z-10 inline-flex max-w-[70%] items-center rounded-full bg-white-500/95 px-2.5 py-1 text-xs font-medium whitespace-nowrap text-ink-700 shadow-[0_2px_8px_-2px_rgba(18,38,63,0.25)] backdrop-blur-sm">
            {isParking ? (
              <span className="font-semibold text-blue-500">
                {t('SEARCH_CARD.SPOTS', { count: numConfigurations })}
              </span>
            ) : (
              <>
                {hasMultipleConfigurations && (
                  <span className="font-semibold text-blue-500">
                    {numConfigurations}
                    <span className="mx-1 text-ink-200">·</span>
                  </span>
                )}
                {sizeText}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5 border-t border-ink-100/15 p-2.5 md:p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold tracking-[-0.005em] text-ink-700 md:text-base">
              {locType ? `${locType}` : ''}
            </p>
            <p className="mt-0.5 line-clamp-1 text-xs text-ink-200">
              {shortAddress}
            </p>
          </div>
        </div>

        {/* Price only, one line — the discount overlays the photo above. */}
        <div className="flex items-end">
          <span className="text-blue-500">
            {showFromPrice && (
              <span className="text-xs font-medium text-ink-200">
                {`${t('SEARCH_CARD.FROM_PRICE')} `}
              </span>
            )}
            {/* Amount + unit never split; only "Desde" may wrap above. */}
            <span className="whitespace-nowrap">
              {iconBig}
              <span className="font-display text-lg font-medium tracking-[-0.01em] italic">
                {`$${hidePrice ? 'X' : numberRounedUpWithoutDecimals(value)}`}
              </span>
              <span className="text-sm font-medium text-ink-200">{unit}</span>
            </span>
          </span>
        </div>
      </div>
    </div>
  )

  // disableLink — render the bare card (some callers render the card
  // inside their own anchor / drawer trigger; double-wrapping would
  // produce invalid HTML). The wrapping caller owns the click event in
  // that case, so this component deliberately fires nothing here.
  if (disableLink) return cardContent

  const trackCardClick = () =>
    ga.event('recommended_card_click', {
      surface,
      listing_id: location.id
    })

  // Desktop: open in a new tab. Matches the historical behavior of
  // CardLocationLink — the card was never a same-tab nav on desktop.
  if (isUpMd) {
    return (
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackCardClick}
        className="block"
      >
        {cardContent}
      </Link>
    )
  }

  // Mobile: real <Link> so the native anchor href navigates
  // pre-hydration. JS path takes over once hydrated to preserve
  // scroll/drawer state for back-nav. `simpleLink` skips the scroll
  // dance entirely — the Link's default navigation is enough.
  return (
    <Link
      href={url}
      onClick={(e) => {
        trackCardClick()
        // onMobileLinkClick self-guards on `simpleLink` (and on isUpMd /
        // modifier keys) — no second guard needed here.
        onMobileLinkClick(e)
      }}
      className="block"
    >
      {cardContent}
    </Link>
  )
}

export default React.memo(SpotCard)
